/**
 * Which of a campaign's links will carry its name to the website — the console's
 * mirror of `utils/campaignLinks.js` in the service.
 *
 * The send tags links there; this exists so the builder can say which ones
 * BEFORE it goes, as the operator types, without a round trip per keystroke. It
 * must agree with the service case for case — a preview promising "3 links will
 * be tagged" over a send that tags none of them is a campaign whose visits never
 * reach the report it was set up for — so `campaignLinks.test.ts` and
 * `scripts/verify/campaignlinks.js` pin the same cases. Change one, change both.
 *
 * The rules, in brief (the service file says why): only the tenant's own website
 * (subdomains included, `www.` folded); a link already carrying any `utm_*` is
 * the operator's and is left alone; the parameters are appended to the string
 * as written, never re-serialised; email only.
 */

export const UTM_SOURCE = 'newsletter';
export const UTM_MEDIUM = 'email';
export const TRACKING_NAME_MAX = 80;

/** The value as the report will spell it: lower case, hyphenated, nothing a URL would escape. */
export const tidyTrackingName = (raw: string | null | undefined): string =>
  String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9._~-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '')
    .slice(0, TRACKING_NAME_MAX);

export const hostKey = (host: string | null | undefined): string =>
  String(host || '')
    .toLowerCase()
    .replace(/^www\./, '');

/** The host of something typed as a website — `example.com`, `https://www.example.com/`. */
export const hostOf = (raw: string | null | undefined): string | null => {
  const text = String(raw ?? '').trim();
  if (!text) return null;
  try {
    const url = new URL(/^https?:\/\//i.test(text) ? text : `https://${text}`);
    return hostKey(url.hostname) || null;
  } catch {
    return null;
  }
};

export const siteHosts = (urls: (string | null | undefined)[]): string[] => {
  const out: string[] = [];
  for (const raw of urls) {
    const host = hostOf(raw);
    if (host && !out.includes(host)) out.push(host);
  }
  return out;
};

export const isOwnHost = (host: string, hosts: string[]): boolean => {
  const key = hostKey(host);
  return Boolean(key) && hosts.some((own) => key === own || key.endsWith(`.${own}`));
};

export interface TagOptions {
  name: string;
  hosts: string[];
}

/** One URL: the tagged string, or null when it is left as written. */
export const tagUrl = (raw: string, { name, hosts }: TagOptions): string | null => {
  if (!name || !hosts.length) return null;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (!isOwnHost(url.hostname, hosts)) return null;
  for (const key of url.searchParams.keys()) {
    if (key.toLowerCase().startsWith('utm_')) return null;
  }
  const extra = `utm_source=${UTM_SOURCE}&utm_medium=${UTM_MEDIUM}&utm_campaign=${encodeURIComponent(name)}`;
  const hashAt = raw.indexOf('#');
  const base = hashAt === -1 ? raw : raw.slice(0, hashAt);
  const hash = hashAt === -1 ? '' : raw.slice(hashAt);
  const joiner = !base.includes('?') ? '?' : /[?&]$/.test(base) ? '' : '&';
  return `${base}${joiner}${extra}${hash}`;
};

const decodeAttr = (value: string): string =>
  value
    .replace(/&(?:amp|#0*38|#x0*26);/gi, '&')
    .replace(/&(?:quot|#0*34|#x0*22);/gi, '"')
    .replace(/&(?:apos|#0*39|#x0*27);/gi, "'");

const encodeAttr = (value: string, quote: string): string => {
  const amp = value.replace(/&/g, '&amp;');
  return quote === '"' ? amp.replace(/"/g, '&quot;') : amp.replace(/'/g, '&#39;');
};

/** The service's pattern, byte for byte: quoted `href` on `<a>` and `<area>` only. */
const hrefPattern = () => /(<(?:a|area)\b[^>]*?\shref\s*=\s*)(?:"([^"]*)"|'([^']*)')/gi;

export const tagHtml = (html: string, opts: TagOptions): { html: string; tagged: number } => {
  let tagged = 0;
  if (!html || !opts.name || !opts.hosts.length) return { html: html || '', tagged };
  const out = html.replace(hrefPattern(), (match, head: string, double?: string, single?: string) => {
    const quote = double !== undefined ? '"' : "'";
    const next = tagUrl(decodeAttr(double ?? single ?? '').trim(), opts);
    if (!next) return match;
    tagged += 1;
    return `${head}${quote}${encodeAttr(next, quote)}${quote}`;
  });
  return { html: out, tagged };
};

export const tagText = (text: string, opts: TagOptions): { text: string; tagged: number } => {
  let tagged = 0;
  if (!text || !opts.name || !opts.hosts.length) return { text: text || '', tagged };
  const out = text.replace(/https?:\/\/[^\s<>"'()[\]{}]+/gi, (match) => {
    const trail = match.match(/[.,;:!?]+$/)?.[0] ?? '';
    const raw = trail ? match.slice(0, -trail.length) : match;
    const next = tagUrl(raw, opts);
    if (!next) return match;
    tagged += 1;
    return `${next}${trail}`;
  });
  return { text: out, tagged };
};

/**
 * What the builder says about a body's links: how many will carry the name and
 * to which of the tenant's hosts, how many already carry a hand-written tag, and
 * which other sites' links are left alone. Counted on the MERGED body, so a link
 * built from `{{company_website}}` is judged by where it actually goes.
 */
export interface LinkSummary {
  tagged: number;
  taggedHosts: string[];
  handTagged: number;
  other: number;
  otherHosts: string[];
}

export const summarizeLinks = (html: string, hosts: string[]): LinkSummary => {
  const summary: LinkSummary = { tagged: 0, taggedHosts: [], handTagged: 0, other: 0, otherHosts: [] };
  const add = (list: string[], host: string) => {
    if (host && !list.includes(host)) list.push(host);
  };
  for (const match of (html || '').matchAll(hrefPattern())) {
    const raw = decodeAttr(match[2] ?? match[3] ?? '').trim();
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      continue;
    }
    // mailto:, tel: and a `{{tag}}` that never resolved are not web links, and
    // counting them as "left as written" would read as a decision about them.
    if (url.protocol !== 'https:' && url.protocol !== 'http:') continue;
    const host = hostKey(url.hostname);
    if (!isOwnHost(host, hosts)) {
      summary.other += 1;
      add(summary.otherHosts, host);
    } else if ([...url.searchParams.keys()].some((key) => key.toLowerCase().startsWith('utm_'))) {
      summary.handTagged += 1;
    } else {
      summary.tagged += 1;
      add(summary.taggedHosts, host);
    }
  }
  return summary;
};
